import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Package, Eye, Clock, CheckCircle2, Truck, XCircle } from 'lucide-react';
import StoreLayout from '../../components/store/StoreLayout';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { toast } from 'sonner';

export default function OrdersPage() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadOrders();
    }
  }, [user]);

  const loadOrders = async () => {
    try {
      setLoading(true);
      const response = await api.get('/checkout/my-orders');
      setOrders(response.data?.items || []);
    } catch (error) {
      console.error('Error loading orders:', error);
      toast.error('Erreur lors du chargement des commandes');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING_PAYMENT: { label: 'En attente', variant: 'secondary', icon: Clock },
      PAID: { label: 'Payée', variant: 'default', icon: CheckCircle2 },
      PROCESSING: { label: 'En préparation', variant: 'default', icon: Package },
      SHIPPED: { label: 'Expédiée', variant: 'default', icon: Truck },
      DELIVERED: { label: 'Livrée', variant: 'default', icon: CheckCircle2 },
      CANCELLED: { label: 'Annulée', variant: 'destructive', icon: XCircle },
      REFUNDED: { label: 'Remboursée', variant: 'secondary', icon: XCircle },
    };
    
    const config = statusConfig[status] || statusConfig.PENDING_PAYMENT;
    const Icon = config.icon;
    
    return (
      <Badge variant={config.variant} className="gap-1">
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  if (!user) {
    return (
      <StoreLayout>
        <section className="py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-md mx-auto text-center">
            <Package className="h-16 w-16 text-muted-foreground/50 mx-auto mb-6" />
            <h1 className="font-display text-3xl font-bold mb-4">Mes commandes</h1>
            <p className="text-muted-foreground mb-8">
              Connectez-vous pour voir vos commandes
            </p>
            <Button size="lg" asChild>
              <Link to="/login">Se connecter</Link>
            </Button>
          </div>
        </section>
      </StoreLayout>
    );
  }

  if (loading) {
    return (
      <StoreLayout>
        <section className="py-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
            </div>
          </div>
        </section>
      </StoreLayout>
    );
  }

  return (
    <StoreLayout>
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {/* Header */}
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="font-display text-4xl font-bold mb-2">Mes commandes</h1>
                <p className="text-muted-foreground">
                  Suivez et consultez l'historique de vos commandes
                </p>
              </div>
              <Button variant="outline" asChild>
                <Link to="/account">Retour</Link>
              </Button>
            </div>

            {/* Orders List */}
            {orders.length === 0 ? (
              <div className="text-center py-12 border border-border rounded-lg">
                <Package className="h-16 w-16 text-muted-foreground/50 mx-auto mb-4" />
                <h3 className="font-medium text-lg mb-2">Aucune commande</h3>
                <p className="text-muted-foreground mb-6">
                  Vous n'avez pas encore passé de commande
                </p>
                <Button asChild>
                  <Link to="/looks">Découvrir nos looks</Link>
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((order) => (
                  <motion.div
                    key={order.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="border border-border rounded-lg p-6 hover:shadow-md transition-shadow"
                  >
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                      <div>
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="font-medium text-lg">
                            Commande #{order.order_number}
                          </h3>
                          {getStatusBadge(order.status)}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Passée le {new Date(order.created_at).toLocaleDateString('fr-FR', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })}
                        </p>
                      </div>
                      <div className="text-left md:text-right">
                        <p className="font-medium text-lg">
                          {(order.total_cents / 100).toFixed(2)}€
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {order.items?.length || 0} article{order.items?.length > 1 ? 's' : ''}
                        </p>
                      </div>
                    </div>

                    {/* Items Preview */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {order.items?.slice(0, 3).map((item, idx) => (
                        <div key={idx} className="flex items-center gap-2 text-sm bg-muted px-3 py-1 rounded">
                          <span>{item.product_name}</span>
                          {item.quantity > 1 && (
                            <span className="text-muted-foreground">×{item.quantity}</span>
                          )}
                        </div>
                      ))}
                      {order.items?.length > 3 && (
                        <div className="text-sm text-muted-foreground px-3 py-1">
                          +{order.items.length - 3} autre{order.items.length - 3 > 1 ? 's' : ''}
                        </div>
                      )}
                    </div>

                    {/* Tracking Info */}
                    {order.tracking_number && (
                      <div className="bg-muted/50 rounded p-3 mb-4">
                        <p className="text-sm font-medium mb-1">Suivi de livraison</p>
                        <p className="text-sm text-muted-foreground">
                          Numéro de suivi: {order.tracking_number}
                        </p>
                        {order.tracking_url && (
                          <a
                            href={order.tracking_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-primary hover:underline"
                          >
                            Suivre ma commande
                          </a>
                        )}
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" asChild>
                        <Link to={`/order/${order.id}`}>
                          <Eye className="h-4 w-4 mr-2" />
                          Voir les détails
                        </Link>
                      </Button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </section>
    </StoreLayout>
  );
}
